<template>
  <v-card class="meter-data" variant="text" :loading="loading" rounded="xl">
    <!-- 筛选栏 -->
    <div class="filter-bar">
      <div class="filter-item">
        <label class="filter-label date-label">请选择日期</label>
        <el-date-picker v-model="dateRange" type="daterange" range-separator="至" start-placeholder="开始时间"
          end-placeholder="结束时间" value-format="YYYY-MM-DD" :disabled-date="disabledDate" :clearable="false"
          class="filter-date-picker" @change="handleDateChange" />
      </div>
      <div class="filter-item">
        <label class="filter-label">选择电表</label>
        <v-autocomplete v-model="filterForm.combPoint" v-model:search="meterKeyword" :items="meterSelectItems"
          item-title="label" item-value="value" :custom-filter="() => true" placeholder="请选择电表" variant="outlined"
          density="compact" class="filter-select" hide-no-data auto-select-first="exact"
          :menu-props="{ contentClass: 'meter-data-filter-select-menu', maxHeight: 380 }"
          @update:model-value="combPointChanged">
          <!-- 选中态只显示电表名称，类型标签仅在下拉选项里展示 -->
          <template #selection="{ item }">
            <span class="meter-selection-name">{{ item.raw?.label }}</span>
          </template>

          <template #subheader="{ props: groupProps }">
            <v-list-subheader class="meter-group-header">
              {{ groupProps.label }}
              <span class="meter-group-count">{{ groupProps.count }}</span>
            </v-list-subheader>
          </template>

          <template #item="{ item, props: itemProps }">
            <v-list-item v-bind="itemProps" :title="null" class="meter-option"
              :class="{ 'meter-option--all': item.raw?.value === 'all' }">
              <div class="meter-option-main">
                <span class="meter-option-name">{{ item.raw?.label }}</span>
                <span v-if="item.raw?.typeLabel" class="meter-tag" :class="item.raw.typeClass">
                  {{ item.raw.typeLabel }}
                </span>
              </div>
            </v-list-item>
          </template>
        </v-autocomplete>
      </div>
      <div class="filter-item" v-if="intervalEnabled">
        <label class="filter-label">时间间隔</label>
        <el-select v-model="filterForm.interval" placeholder="原始粒度" class="interval-select"
          popper-class="meter-data-interval-dropdown" @change="handleIntervalChange">
          <el-option label="原始粒度" value="" />
          <el-option label="15分钟" value="15m" />
          <el-option label="1小时" value="1h" />
        </el-select>
      </div>
      <div class="filter-item" v-if="intervalEnabled">
        <label class="filter-label">数据模式</label>
        <el-select
          v-model="filterForm.diff"
          class="interval-select"
          popper-class="meter-data-interval-dropdown"
          :disabled="!canEnableDiff"
          @change="handleDiffChange"
        >
          <el-option label="原始值" :value="false" />
          <el-option label="差值" :value="true" :disabled="!canEnableDiff" />
        </el-select>
        <el-tooltip v-if="!canEnableDiff" content="请先选择 15分钟 或 1小时 时间间隔" placement="top">
          <el-icon class="tip-icon"><QuestionFilled /></el-icon>
        </el-tooltip>
      </div>
      <div class="filter-actions">
        <v-btn color="primary" class="query-btn" @click="handleSearch">
          <img src="@/assets/icons/svg/search-query.svg" alt="查询" class="query-icon" />
          查询
        </v-btn>
        <v-btn variant="outlined" class="export-btn" @click="handleExport">
          <img src="@/assets/icons/svg/export.svg" alt="导出" class="export-icon" />
          导出
        </v-btn>
        <v-btn variant="outlined" class="export-day-btn" @click="handleExportDayData">
          <img src="@/assets/icons/svg/export.svg" alt="导出日报表" class="export-icon" />
          导出日报表
        </v-btn>
      </div>
    </div>

    <!-- 表格容器 -->
    <div class="table-container">
      <div class="table-wrapper">
        <v-data-table :headers="tableHeaders" :items="paginatedData" :loading="loading"
          :items-per-page="pagination.pageSize" :page="pagination.current" class="meter-table gm-table" item-value="key"
          hide-default-footer hide-default-header no-data-text="暂无数据">
          <!-- 自定义表头 -->
          <template #top>
            <table class="custom-header-table">
              <thead>
                <tr>
                  <th rowspan="2" class="table-header record-time-header">读表时间</th>
                  <th colspan="6" class="table-header group-header">正向有功电能</th>
                  <th colspan="6" class="table-header group-header">反向有功电能</th>
                </tr>
                <tr>
                  <th class="table-header sub-header">尖峰时段</th>
                  <th class="table-header sub-header">高峰时段</th>
                  <th class="table-header sub-header">平时段</th>
                  <th class="table-header sub-header">低谷时段</th>
                  <th class="table-header sub-header">深谷时段</th>
                  <th class="table-header sub-header">合计</th>
                  <th class="table-header sub-header">尖峰时段</th>
                  <th class="table-header sub-header">高峰时段</th>
                  <th class="table-header sub-header">平时段</th>
                  <th class="table-header sub-header">低谷时段</th>
                  <th class="table-header sub-header">深谷时段</th>
                  <th class="table-header sub-header">合计</th>
                </tr>
              </thead>
            </table>
          </template>

          <!-- 读表时间 -->
          <template #item.recordTime="{ item }">
            <span class="table-cell">{{ formatDate(item.recordTime) }}</span>
          </template>

          <!-- 正向有功电能 - 尖峰时段 -->
          <template #item.ePositiveSharp="{ item }">
            <span class="table-cell">{{ item.ePositiveSharp }}</span>
          </template>

          <!-- 正向有功电能 - 高峰时段 -->
          <template #item.ePositivePeak="{ item }">
            <span class="table-cell">{{ item.ePositivePeak }}</span>
          </template>

          <!-- 正向有功电能 - 平时段 -->
          <template #item.ePositiveFlat="{ item }">
            <span class="table-cell">{{ item.ePositiveFlat }}</span>
          </template>

          <!-- 正向有功电能 - 低谷时段 -->
          <template #item.ePositiveValley="{ item }">
            <span class="table-cell">{{ item.ePositiveValley }}</span>
          </template>

          <!-- 正向有功电能 - 深谷时段 -->
          <template #item.ePositiveDeepValley="{ item }">
            <span class="table-cell">{{ item.ePositiveDeepValley }}</span>
          </template>

          <!-- 正向有功电能 - 合计 -->
          <template #item.ePositive="{ item }">
            <span class="table-cell">{{ item.ePositive }}</span>
          </template>

          <!-- 反向有功电能 - 尖峰时段 -->
          <template #item.eReverseSharp="{ item }">
            <span class="table-cell">{{ item.eReverseSharp }}</span>
          </template>

          <!-- 反向有功电能 - 高峰时段 -->
          <template #item.eReversePeak="{ item }">
            <span class="table-cell">{{ item.eReversePeak }}</span>
          </template>

          <!-- 反向有功电能 - 平时段 -->
          <template #item.eReverseFlat="{ item }">
            <span class="table-cell">{{ item.eReverseFlat }}</span>
          </template>

          <!-- 反向有功电能 - 低谷时段 -->
          <template #item.eReverseValley="{ item }">
            <span class="table-cell">{{ item.eReverseValley }}</span>
          </template>

          <!-- 反向有功电能 - 深谷时段 -->
          <template #item.eReverseDeepValley="{ item }">
            <span class="table-cell">{{ item.eReverseDeepValley }}</span>
          </template>

          <!-- 反向有功电能 - 合计 -->
          <template #item.eReverse="{ item }">
            <span class="table-cell">{{ item.eReverse }}</span>
          </template>
        </v-data-table>
      </div>
    </div>

    <!-- 分页 -->
    <!-- <div class="pagination-container">
      <div class="pagination-info">共 {{ pagination.total }} 项数据</div>
      <div class="pagination-controls">
        <v-select v-model="pagination.pageSize" :items="pageSizeItems" variant="outlined" density="compact"
          class="page-size-select" hide-details @update:model-value="handleSizeChange" />
        <v-pagination v-model="pagination.current" :length="totalPages" :total-visible="7" class="custom-pagination"
          @update:model-value="handlePageChange" />
        <div class="jump-to-page">
          <span>跳至</span>
          <v-text-field v-model.number="jumpPage" type="number" variant="outlined" density="compact" class="jump-input"
            hide-details @keyup.enter="handleJumpToPage" @blur="handleJumpToPage" />
          <span>/{{ totalPages }} 页</span>
        </div>
      </div>
    </div> -->

    <TablePagination v-model="pagination.current" :total="pagination.total" v-model:page-size="pagination.pageSize"
      @change="() => fetchMeterDataList()" :hasJump="true" :hasSizes="true" :disabled="loading" />


  </v-card>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import dayjs from 'dayjs'
import { ElMessage } from 'element-plus'
import { QuestionFilled } from '@element-plus/icons-vue'
import { getMeterList, getMeterDataList, exportMeterDataRecord, exportOneDayMeterDataRecord } from '@/api/meterData'
import { list } from '@/api/device/meter'
import { useStationContext } from '../composables/useStationContext'
import useAppStore from '@/store/modules/app'

import TablePagination from '@/components/TablePagination/index.vue'

// 使用 provide/inject 获取电站信息
const { stationId } = useStationContext()
const appStore = useAppStore()

/**
 * 按间隔统计电量是否开启（deviceFlag bit5）。
 * 未开启的电站不显示时间间隔与数据模式控件，接口按原始粒度查询。
 * store getter 是三态，这里只认 true。
 */
const intervalEnabled = computed(() => appStore.spotSettlementEnabled === true)

// 筛选表单
// 日期范围（Element Plus 格式：['YYYY-MM-DD', 'YYYY-MM-DD']）
const dateRange = ref([])
const dateRangeStart = ref('')
const dateRangeEnd = ref('')
const filterForm = reactive({
  combPoint: '',
  interval: '',     // 时间间隔：'' 原始粒度 / '15m' / '1h'
  diff: false       // 差值模式：false 原始累计值 / true 增量
})

// 差值模式只在选了 15m 或 1h 时才能打开
const canEnableDiff = computed(() => {
  return filterForm.interval === '15m' || filterForm.interval === '1h'
})

// 时间间隔变化时的联动逻辑
function handleIntervalChange(val) {
  // 切到原始粒度时，自动关闭差值模式
  if (!val || (val !== '15m' && val !== '1h')) {
    filterForm.diff = false
  }
  // 切换完成后自动查询
  handleSearch()
}

// 差值模式变化时自动查询
function handleDiffChange(val) {
  // 差值模式变化后自动查询（不改变时间间隔）
  handleSearch()
}

// 电站不支持按间隔统计时，重置为原始粒度，避免带着上一个电站的参数查询
watch(intervalEnabled, (enabled) => {
  if (!enabled && (filterForm.interval || filterForm.diff)) {
    filterForm.interval = ''
    filterForm.diff = false
  }
})

// 电表列表（扁平的原始电表数据，不含分组项）
const meterList = ref([])
// 下拉框搜索关键字
const meterKeyword = ref('')

const ALL_METERS_LABEL = '全部电表'

const METER_TYPE_GROUP_LABELS = {
  1: '关口电表',
  2: '计量电表',
  3: '并网点电表',
  4: '负载表',
  9: '总关口'
}

// 分组展示顺序：总关口 → 关口 → 并网点 → 计量 → 负载 → 其他
const METER_TYPE_ORDER = [9, 1, 3, 2, 4]

// 并网点电表类型，进入页面时默认选中该类型的第一只电表
const GRID_METER_TYPE = 3

const METER_TYPE_CLASSES = {
  9: 'is-main',
  1: 'is-gateway',
  3: 'is-grid',
  2: 'is-measure',
  4: 'is-load'
}

function getMeterTypeGroupLabel(meterType) {
  const n = Number(meterType)
  if (meterType == null || meterType === '' || Number.isNaN(n)) return '其他'
  return METER_TYPE_GROUP_LABELS[n] || `电表类型 ${meterType}`
}

/** 副标题行：型号 · 设备ID，用于区分同名电表 */
function getMeterMeta(meter) {
  return [meter.productModel, meter.devId].filter(Boolean).join(' · ')
}

/** 把接口返回的电表转成下拉选项，按类型排序并附带展示字段 */
function buildMeterSelectItems(meters) {
  return meters
    .filter((m) => !!(m.uuid || m.id))
    .map((m) => {
      const n = Number(m.meterType)
      const known = !Number.isNaN(n) && m.meterType != null && m.meterType !== ''
      return {
        label: m.meterName || m.name || '未命名电表',
        value: m.uuid || m.id,
        meterType: known ? n : null,
        typeLabel: getMeterTypeGroupLabel(m.meterType),
        typeClass: METER_TYPE_CLASSES[n] || 'is-other',
        meta: getMeterMeta(m)
      }
    })
    .sort((a, b) => {
      const ai = METER_TYPE_ORDER.indexOf(a.meterType)
      const bi = METER_TYPE_ORDER.indexOf(b.meterType)
      const ao = ai === -1 ? METER_TYPE_ORDER.length : ai
      const bo = bi === -1 ? METER_TYPE_ORDER.length : bi
      if (ao !== bo) return ao - bo
      return a.label.localeCompare(b.label, 'zh-CN')
    })
}

/** 关键字过滤 + 插入分组标题，供下拉框渲染 */
const meterSelectItems = computed(() => {
  const keyword = String(meterKeyword.value || '').trim().toLowerCase()
  const selectedLabel =
    filterForm.combPoint === 'all'
      ? ALL_METERS_LABEL
      : meterList.value.find((m) => m.value === filterForm.combPoint)?.label
  // 选中后 v-autocomplete 会把 search 同步为选中项文本，此时不应过滤
  const active = keyword && keyword !== String(selectedLabel || '').toLowerCase()

  const matched = active
    ? meterList.value.filter(
      (m) =>
        m.label.toLowerCase().includes(keyword) ||
        m.meta.toLowerCase().includes(keyword) ||
        m.typeLabel.toLowerCase().includes(keyword)
    )
    : meterList.value

  const items = []
  if (!active) {
    items.push({
      label: ALL_METERS_LABEL,
      value: 'all',
      typeLabel: '',
      typeClass: '',
      meta: `共 ${meterList.value.length} 只电表`
    })
  }

  let currentType
  for (const m of matched) {
    if (m.meterType !== currentType) {
      currentType = m.meterType
      items.push({
        type: 'subheader',
        label: m.typeLabel,
        value: `group-${m.meterType}`,
        count: matched.filter((x) => x.meterType === m.meterType).length
      })
    }
    items.push(m)
  }

  return items
})

// 表格数据
const tableData = ref([])
const loading = ref(false)

// 表格列定义
const tableHeaders = [
  { title: '读表时间', key: 'recordTime', align: 'start', width: '170px' },
  { title: '尖峰时段', key: 'ePositiveSharp', align: 'center', width: '120px' },
  { title: '高峰时段', key: 'ePositivePeak', align: 'center', width: '120px' },
  { title: '平时段', key: 'ePositiveFlat', align: 'center', width: '120px' },
  { title: '低谷时段', key: 'ePositiveValley', align: 'center', width: '120px' },
  { title: '深谷时段', key: 'ePositiveDeepValley', align: 'center', width: '120px' },
  { title: '合计', key: 'ePositive', align: 'center', width: '120px' },
  { title: '尖峰时段', key: 'eReverseSharp', align: 'center', width: '120px' },
  { title: '高峰时段', key: 'eReversePeak', align: 'center', width: '120px' },
  { title: '平时段', key: 'eReverseFlat', align: 'center', width: '120px' },
  { title: '低谷时段', key: 'eReverseValley', align: 'center', width: '120px' },
  { title: '深谷时段', key: 'eReverseDeepValley', align: 'center', width: '120px' },
  { title: '合计', key: 'eReverse', align: 'center', width: '120px' }
]

// 分页
const pagination = reactive({
  current: 1,
  pageSize: 25,
  total: 0
})

const jumpPage = ref(1)

// 计算总页数，如果没有数据也显示一页
const totalPages = computed(() => {
  const pages = Math.ceil(pagination.total / pagination.pageSize)
  return pages === 0 ? 1 : pages
})

// 分页后的数据
const paginatedData = computed(() => {
  return tableData.value
})

// 每页条数选项
const pageSizeItems = [
  { title: '25 条/页', value: 25 },
  { title: '40 条/页', value: 40 },
  { title: '50 条/页', value: 50 },
  { title: '100 条/页', value: 100 }
]

// 设置日期的禁选范围（半年前到今天）
function disabledDate(date) {
  const today = dayjs()
  const sixMonthsAgo = today.subtract(6, 'month')
  return dayjs(date) > today || dayjs(date) < sixMonthsAgo
}

// 处理日期范围变化
function handleDateChange(value) {
  if (value && Array.isArray(value) && value.length === 2) {
    dateRangeStart.value = `${value[0]}T00:00`
    dateRangeEnd.value = `${value[1]}T23:59`
  } else {
    dateRangeStart.value = ''
    dateRangeEnd.value = ''
  }
}


// 格式化日期时间为API需要的格式 (YYYY-MM-DD HH:mm:ss)
const formatDateTimeForAPI = (dateTimeString) => {
  if (!dateTimeString) return undefined
  if (dateTimeString.includes('T')) {
    return dateTimeString.replace('T', ' ') + ':00'
  }
  if (dateTimeString.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/)) {
    return dateTimeString + ':00'
  }
  return dateTimeString
}

// 格式化时间显示
const formatDate = (date) => {
  return dayjs(date).format('YYYY-MM-DD HH:mm:ss')
}

// 更新电表列表
const updateMeterList = async (stationId) => {
  if (!stationId) return
  try {

    // const res = await getMeterList({ stationId })
    const res = await list(1, 655535, stationId, null)
    let meters = []

    // 处理不同的返回格式
    if (res.code === 200 || res.code === '200') {
      if (Array.isArray(res.data)) {
        meters = res.data
      } else if (res.data && Array.isArray(res.data.rows)) {
        meters = res.data.rows
      } else if (Array.isArray(res.rows)) {
        meters = res.rows
      }
    } else if (Array.isArray(res)) {
      meters = res
    }

    meterList.value = buildMeterSelectItems(meters)

    if (meterList.value.length > 0) {
      // 默认选中第一个并网点电表（meterType 3），没有并网点电表时退回「全部电表」
      const firstGridMeter = meterList.value.find((m) => m.meterType === GRID_METER_TYPE)
      filterForm.combPoint = firstGridMeter ? firstGridMeter.value : 'all'
      // 数据加载完成后自动查询
      handleSearch()
    } else {
      filterForm.combPoint = ''
    }
  } catch (error) {
    console.error('获取电表列表失败:', error)
    meterList.value = []
    filterForm.combPoint = ''
  }
}

// 电表变化
const combPointChanged = () => {
  handleSearch()
}

// 获取UUID列表
const getUUIDs = () => {
  if (filterForm.combPoint === 'all') {
    return meterList.value.map((item) => item.value).join(',')
  }
  return filterForm.combPoint || ''
}

// 获取列表数据
const fetchMeterDataList = async () => {
  if (!stationId.value) {
    tableData.value = []
    pagination.total = 0
    return
  }

  if (!filterForm.combPoint || filterForm.combPoint === '') {
    tableData.value = []
    pagination.total = 0
    return
  }

  if (!dateRangeStart.value || !dateRangeEnd.value) {
    return
  }

  // 获取UUID列表
  const uuids = getUUIDs()
  if (!uuids) {
    tableData.value = []
    pagination.total = 0
    return
  }

  loading.value = true
  try {
    const startTimeFormatted = formatDateTimeForAPI(dateRangeStart.value)
    const endTimeFormatted = formatDateTimeForAPI(dateRangeEnd.value)
    const params = {
      pageSize: pagination.pageSize,
      pageNum: pagination.current,
      startTime: startTimeFormatted ? startTimeFormatted.replace(/:\d{2}$/, ':00') : undefined,
      endTime: endTimeFormatted ? endTimeFormatted.replace(/:\d{2}$/, ':59') : undefined,
      stationId: stationId.value,
      uuids,
      // 未开启按间隔统计的电站一律按原始粒度查
      interval: intervalEnabled.value ? (filterForm.interval || undefined) : undefined,
      diff: intervalEnabled.value ? filterForm.diff : false
    }

    const res = await getMeterDataList(params)

    // 处理不同的返回格式
    let rows = []
    let total = 0

    if (res.code === 200 || res.code === '200') {
      rows = res.rows || res.data?.rows || []
      total = res.total || res.data?.total || 0
    } else if (res.data) {
      rows = res.data.rows || []
      total = res.data.total || 0
    } else if (Array.isArray(res)) {
      // 如果直接返回数组
      rows = res
      total = res.length
    }

    tableData.value = rows.map(item => ({
      key: item.id || `${item.recordTime}_${item.uuid || ''}`,
      recordTime: item.recordTime || '',
      ePositiveSharp: item.ePositiveSharp !== undefined && item.ePositiveSharp !== null ? String(item.ePositiveSharp) : '0',
      ePositivePeak: item.ePositivePeak !== undefined && item.ePositivePeak !== null ? String(item.ePositivePeak) : '0',
      ePositiveFlat: item.ePositiveFlat !== undefined && item.ePositiveFlat !== null ? String(item.ePositiveFlat) : '0',
      ePositiveValley: item.ePositiveValley !== undefined && item.ePositiveValley !== null ? String(item.ePositiveValley) : '0',
      ePositiveDeepValley: item.ePositiveDeepValley !== undefined && item.ePositiveDeepValley !== null ? String(item.ePositiveDeepValley) : '0',
      ePositive: item.ePositive !== undefined && item.ePositive !== null ? String(item.ePositive) : '0',
      eReverseSharp: item.eReverseSharp !== undefined && item.eReverseSharp !== null ? String(item.eReverseSharp) : '0',
      eReversePeak: item.eReversePeak !== undefined && item.eReversePeak !== null ? String(item.eReversePeak) : '0',
      eReverseFlat: item.eReverseFlat !== undefined && item.eReverseFlat !== null ? String(item.eReverseFlat) : '0',
      eReverseValley: item.eReverseValley !== undefined && item.eReverseValley !== null ? String(item.eReverseValley) : '0',
      eReverseDeepValley: item.eReverseDeepValley !== undefined && item.eReverseDeepValley !== null ? String(item.eReverseDeepValley) : '0',
      eReverse: item.eReverse !== undefined && item.eReverse !== null ? String(item.eReverse) : '0'
    }))
    pagination.total = total

  } catch (error) {
    console.error('获取电表数据失败:', error)
    tableData.value = []
    pagination.total = 0
  } finally {
    loading.value = false
  }
}

// 查询
const handleSearch = () => {
  fetchMeterDataList()
}

// 导出
const handleExport = async () => {

  try {
    const uuids = getUUIDs()
    const startTimeFormatted = formatDateTimeForAPI(dateRangeStart.value)
    const endTimeFormatted = formatDateTimeForAPI(dateRangeEnd.value)
    const params = {
      startTime: startTimeFormatted ? startTimeFormatted.replace(/:\d{2}$/, ':00') : undefined,
      endTime: endTimeFormatted ? endTimeFormatted.replace(/:\d{2}$/, ':59') : undefined,
      stationId: stationId.value,
      uuids,
      interval: intervalEnabled.value ? (filterForm.interval || undefined) : undefined,
      diff: intervalEnabled.value ? filterForm.diff : false
    }
    await exportMeterDataRecord(params)
  } catch (error) {
    console.error('导出电表数据失败:', error)
  }
}

// 导出日报表
const handleExportDayData = async () => {
  // 判断日期范围是否为一天
  const sd = dayjs(dateRangeStart.value)
  const ed = dayjs(dateRangeEnd.value)
  if (!sd.isSame(ed, 'day')) {
    ElMessage.warning('导出日报表时，日期范围必须为同一天')
    return
  }

  try {
    const uuids = getUUIDs()
    const startTimeFormatted = formatDateTimeForAPI(dateRangeStart.value)
    const endTimeFormatted = formatDateTimeForAPI(dateRangeEnd.value)
    const params = {
      startTime: startTimeFormatted ? startTimeFormatted.replace(/:\d{2}$/, ':00') : undefined,
      endTime: endTimeFormatted ? endTimeFormatted.replace(/:\d{2}$/, ':59') : undefined,
      stationId: stationId.value,
      uuids
    }
    await exportOneDayMeterDataRecord(params)
  } catch (error) {
    console.error('导出电表数据日报表失败:', error)
  }
}

// 分页变化
const handleSizeChange = (size) => {
  pagination.pageSize = size
  pagination.current = 1
  jumpPage.value = 1
  fetchMeterDataList()
}

const handlePageChange = (page) => {
  pagination.current = page
  jumpPage.value = page
  fetchMeterDataList()
}

// 跳转到指定页
const handleJumpToPage = () => {
  if (jumpPage.value >= 1 && jumpPage.value <= totalPages.value) {
    pagination.current = jumpPage.value
    fetchMeterDataList()
  } else {
    // 如果输入无效，重置为当前页
    jumpPage.value = pagination.current
  }
}

// 监听 stationId 变化
watch(() => stationId.value, (newVal) => {
  if (newVal) {
    appStore.getBaseData(newVal)
    updateMeterList(newVal)
  } else {
    meterList.value = []
    filterForm.combPoint = ''
  }
})

onMounted(async () => {
  // 初始化日期为今天
  const today = dayjs()
  const todayStr = today.format('YYYY-MM-DD')
  dateRange.value = [todayStr, todayStr]
  dateRangeStart.value = `${todayStr}T00:00`
  dateRangeEnd.value = `${todayStr}T23:59`

  if (stationId.value) {
    // 主动刷新电站基础信息：同一电站内切 Tab 时 StationStatusBar 不会重新挂载，
    // deviceFlag 会停留在 localStorage 的旧值，导致刚勾选的能力位读不到。
    // store 内部有在途去重，不会与 StationStatusBar 重复请求。
    appStore.getBaseData(stationId.value)
    await updateMeterList(stationId.value)
  }
})
</script>

<style scoped lang="scss">
.meter-data {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 100%;
  min-width: 0;
  gap: 0;
  padding: 0;
  background: #fff;
  overflow: hidden;
  padding-bottom: 16px;
  box-sizing: border-box;
}

.filter-bar {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  padding: 16px 24px;
  background: #fff;
  flex-shrink: 0;
  flex-wrap: wrap;
  overflow-x: visible;
  min-height: 72px;
  min-width: 0;

  :deep(.v-select__selection-text) {
    max-width: none;
  }
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 0 1 auto;
  min-width: 0;
  margin-right: 12px;
  height: 40px;
}

.filter-label {
  font-family: 'PingFang SC', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #2e2e2e;
  white-space: nowrap;
  flex-shrink: 0;
  line-height: 40px;
  height: 40px;
  display: flex;
  align-items: center;
}

.filter-label.date-label {
  color: #2E2E2E;
  font-family: "HarmonyOS Sans SC", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.filter-date-picker {
  width: 230px !important;
  min-width: min(230px, 100%) !important;
  max-width: 100% !important;

  :deep(.el-input__wrapper) {
    background: #fff;
    border: 1px solid rgba(192, 207, 218, 0.3);
    border-radius: 20px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    padding: 0 16px;
    min-height: 40px;
    height: 40px;
    transition: border-color 0.2s;

    &.is-focus {
      border-color: #3a68ff;
    }
  }

  :deep(.el-input__inner) {
    font-family: "HarmonyOS Sans SC", sans-serif;
    font-size: 14px;
    color: #2E2E2E;
    height: 40px;
    line-height: 40px;

    &::placeholder {
      color: rgba(46, 46, 46, 0.5);
      font-weight: 400;
      font-family: "HarmonyOS Sans SC", sans-serif;
    }
  }

  :deep(.el-input__suffix) {
    color: rgba(46, 46, 46, 0.5);
  }
}

:deep(.el-range-editor) {
  width: 230px !important;
  min-width: min(230px, 100%) !important;
  max-width: 100% !important;
  background: #fff;
  border: 2px solid #c0cfda4d;
  border-radius: 20px;
  align-items: center;
  margin: 0;
  display: flex;
  box-shadow: 0 1px 2px #0000000d;
  height: 40px !important;
  min-height: 40px !important;
  max-height: 40px !important;
}
:deep(.el-range-editor:focus-within) {
  border-color: var(--color-primary);
}


:deep(.el-date-editor) {
  .el-range__close-icon {
    display: none !important;
  }
}

// 选中日期时的颜色
:deep(.el-date-editor.el-input__wrapper:hover) {
  box-shadow: none !important;
}

.filter-select {
  width: 280px;
  min-width: min(280px, 100%);
  max-width: 100%;

  :deep(.v-field) {
    min-height: 40px !important;
    height: 40px !important;
    max-height: 40px !important;
    background: #fff;
    border: 1px solid rgba(192, 207, 218, 0.3);
    border-radius: 20px;
    display: flex;
    align-items: center;
    margin: 0;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    min-width: 100%;
  }

  :deep(.v-field__input) {
    padding: 8px 0px 8px 16px;
    font-family: "HarmonyOS Sans SC", sans-serif;
    font-size: 15px;
    display: flex;
    align-items: center;
    min-width: 0;
    flex: 1;
  }

  // :deep(.v-field__outline) {
  //   display: none;
  // }

  :deep(.v-select__selection) {
    font-family: "HarmonyOS Sans SC", sans-serif;
    font-size: 15px;
    color: #2E2E2E;
    line-height: 24px;
    display: flex;
    align-items: center;
    white-space: nowrap;
    overflow: hidden;
    max-width: none;
    flex: 1;
    min-width: 0;
  }

  // 选中态：名称过长时省略而非撑破筛选栏
  .meter-selection-name {
    display: block;
    min-width: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }


  :deep(.v-select__selection-text) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: none;
    flex: 1;
    min-width: 0;
  }

  :deep(.v-field__append-inner) {
    flex-shrink: 0;
  }

  :deep(.v-field__append-inner .v-icon) {
    font-size: 16px !important;
    width: 16px !important;
    height: 16px !important;
    color: #2E2E2E !important;
    opacity: 0.22;
  }
}

/* Element Plus 2.6+ 的 el-select 渲染的是 .el-select__wrapper（不再是 .el-input__wrapper），
   这里对齐左侧日期选择器 / 电表下拉的圆角、高度与字号 */
.interval-select {
  width: 140px;

  :deep(.el-select__wrapper) {
    background: #fff;
    border: 1px solid rgba(192, 207, 218, 0.3);
    border-radius: 20px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    padding: 0 16px;
    min-height: 40px;
    height: 40px;
    line-height: 40px;
    font-family: "HarmonyOS Sans SC", sans-serif;
    font-size: 15px;
    transition: border-color 0.2s;

    // EP 默认用 inset box-shadow 画描边，会盖掉上面的 border，需要一并压掉
    &.is-hovering,
    &.is-focused {
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    }

    &.is-focused {
      border-color: #3a68ff;
    }

    &.is-disabled {
      background: #f7f8fa;
      box-shadow: none;
    }
  }

  :deep(.el-select__placeholder) {
    font-family: "HarmonyOS Sans SC", sans-serif;
    font-size: 15px;
    color: #2E2E2E;

    &.is-transparent {
      color: rgba(46, 46, 46, 0.5);
    }
  }

  :deep(.el-select__suffix .el-icon) {
    color: rgba(46, 46, 46, 0.22);
  }
}

.tip-icon {
  margin-left: 6px;
  font-size: 16px;
  color: rgba(46, 46, 46, 0.5);
  cursor: pointer;
  vertical-align: middle;
}

.filter-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
  flex: 0 1 auto;
  min-width: 0;
  height: 40px;
  flex-wrap: wrap;
}

.query-btn {
  width: 98px;
  height: 40px !important;
  min-height: 40px !important;
  max-height: 40px !important;
  border-radius: 12px !important;
  font-family: "HarmonyOS Sans SC", sans-serif;
  font-size: 14px;
  font-weight: 400;
  background: #3a68ff !important;
  color: #fff !important;
  text-transform: none !important;
  padding: 0 !important;
  border: none !important;
  box-shadow: none !important;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  .query-icon {
    width: 13px;
    height: 13px;
    margin-inline-end: 6px;
    display: inline-block;
    flex-shrink: 0;
  }

  &:hover {
    background: #3a68ff !important;
    opacity: 0.9;
  }

  &:active {
    background: #3a68ff !important;
    opacity: 0.8;
  }
}

.export-btn,
.export-day-btn {
  width: 120px;
  height: 40px !important;
  min-height: 40px !important;
  max-height: 40px !important;
  border-radius: 12px !important;
  border: 1px solid rgba(192, 207, 218, 0.3) !important;
  font-family: "HarmonyOS Sans SC", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #2e2e2e !important;
  background: #fff !important;
  text-transform: none !important;
  padding: 0 !important;
  box-shadow: none !important;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  .export-icon {
    width: 12px;
    height: 12px;
    margin-inline-end: 6px;
    display: inline-block;
    flex-shrink: 0;
  }
}

.table-container {
  flex: 1;
  min-height: 0;
  // background: #fff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 0;
}

.table-wrapper {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0 24px;
  min-width: 0;
}

.custom-header-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 0;

  thead {
    tr {
      th {
        background: transparent !important;
        color: #96a5b8;
        font-family: "HarmonyOS Sans SC", sans-serif;
        font-size: 13px;
        font-weight: 400;
        padding: 12px 16px;
        border: 1px solid rgba(192, 207, 218, 0.3);
        border-top: none;
        line-height: 16px;
        text-align: center;
        vertical-align: middle;
        position: relative;

        &.record-time-header {
          text-align: left;
          border-left: none;
        }

        &.group-header {
          font-weight: 500;
          color: #2e2e2e;
          border-bottom: 1px solid rgba(192, 207, 218, 0.5);
        }

        &.sub-header {
          font-weight: 400;
          color: #96a5b8;
          border-top: none;
        }

        &:first-child {
          border-left: none;
        }

        &:last-child {
          border-right: none;
        }
      }

      &:first-child th {
        border-top: 1px solid rgba(192, 207, 218, 0.3);
      }
    }
  }
}

.meter-table {
  width: 100%;
  flex: 1;
  min-height: 0;
  background: transparent;

  :deep(.v-data-table__tbody) {
    tbody {
      tr {
        td {
          color: #444a6d;
          font-family: 'PingFang SC', sans-serif;
          font-size: 14px;
          font-weight: 400;
          padding: 12px 16px;
          border: none;
          border-left: 1px solid rgba(192, 207, 218, 0.1);
          border-right: 1px solid rgba(192, 207, 218, 0.1);
          line-height: 1.5;
          text-align: center;

          &:first-child {
            text-align: left;
            border-left: none;
          }

          &:last-child {
            border-right: none;
          }
        }

        &:nth-child(even) {
          background: #edf2f6 !important;
        }

        &:hover {
          background: #f0f4f8 !important;
        }
      }
    }
  }

  :deep(.v-data-table__wrapper) {
    overflow-x: auto;
  }
}

.loading-container,
.no-data-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: rgba(0, 0, 0, 0.6);
  font-family: 'PingFang SC', sans-serif;
  font-size: 14px;
}

.loading-container {
  flex-direction: column;
  gap: 16px;
}

.loading-text {
  color: rgba(0, 0, 0, 0.6);
  font-family: 'PingFang SC', sans-serif;
  font-size: 14px;
}

.pagination-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: #fff;
  flex-shrink: 0;
  border-top: 1px solid #f0f0f0;

  :deep(.v-btn--disabled) {
    color: #fff !important;
    opacity: 1 !important;
  }
}

.pagination-info {
  font-family: 'PingFang SC', sans-serif;
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  line-height: 22px;
  letter-spacing: 0%;
  color: rgba(0, 0, 0, 0.6);
  vertical-align: middle;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 16px;
  height: 100%;
}

.page-size-select {
  width: 112px;
  flex-shrink: 0;

  :deep(.v-field) {
    background: #fff !important;
    border: 1px solid #dcdcdc !important;
    border-radius: 3px !important;
    min-height: auto !important;
    height: auto !important;
    padding: 5px 8px !important;
    box-shadow: none !important;
    display: flex !important;
    align-items: center !important;
  }

  :deep(.v-field__input) {
    padding: 0 !important;
    font-family: 'PingFang SC', sans-serif !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    color: rgba(0, 0, 0, 0.9) !important;
    min-height: auto !important;
    height: 22px !important;
    line-height: 22px !important;
    display: flex !important;
    align-items: center !important;
  }

  :deep(.v-select__selection) {
    font-family: 'PingFang SC', sans-serif !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    color: rgba(0, 0, 0, 0.9) !important;
    line-height: 22px !important;
    margin: 0 !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :deep(.v-field__outline) {
    display: none !important;
  }

  :deep(.v-field__append-inner .v-icon) {
    font-size: 16px !important;
    width: 16px !important;
    height: 16px !important;
    color: #2E2E2E !important;
    opacity: 0.22;
  }
}

.custom-pagination {
  flex-shrink: 0;

  :deep(.v-pagination__list) {
    align-items: center;
    gap: 8px !important;
    margin: 0 !important;
  }

  :deep(.v-btn) {
    min-width: 32px !important;
    width: 32px !important;
    height: 32px !important;
    border-radius: 3px !important;
    margin: 0 !important;
    font-family: 'PingFang SC', sans-serif !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    line-height: 22px !important;
    border: none !important;
    background: transparent !important;
    color: #000000E6 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 0 !important;
    transition: none !important;
    box-shadow: none !important;
    text-align: center !important;

    &:hover {
      background: transparent !important;
      color: #000000E6 !important;
    }

    &.v-pagination__item--is-active {
      color: #000000E6 !important;
      border: none !important;
      box-shadow: none !important;
    }

    .v-pagination__item--text {
      line-height: 22px !important;
      font-family: 'PingFang SC', sans-serif !important;
      font-size: 14px !important;
      color: #000000E6 !important;
    }
  }

  :deep(.v-pagination__prev),
  :deep(.v-pagination__next) {
    min-width: 32px !important;
    width: 32px !important;
    height: 32px !important;
    border-radius: 3px !important;
    border: none !important;
    background: transparent !important;
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    transition: none !important;
    box-shadow: none !important;

    &:hover:not(.v-pagination__item--disabled) {
      background: transparent !important;
    }

    &.v-pagination__item--disabled {
      opacity: 0.4 !important;
      cursor: not-allowed !important;
    }
  }

  :deep(.v-pagination__prev .v-icon),
  :deep(.v-pagination__next .v-icon) {
    font-size: 16px !important;
    width: 16px !important;
    height: 16px !important;
    color: rgba(0, 0, 0, 1) !important;
  }

  /* 省略号（ellipsis）样式 */
  :deep(.v-pagination__item--ellipsis) {
    border: none !important;
    background: #fff !important;
    color: #000000E6 !important;
    font-family: 'PingFang SC', sans-serif !important;
    font-size: 14px !important;
  }

  :deep(.v-btn--variant-text) {
    border: none !important;
    background: transparent !important;
    color: #000000E6 !important;
    font-family: 'PingFang SC', sans-serif !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    box-shadow: none !important;

    &:active,
    &:focus {
      box-shadow: none !important;
      background: transparent !important;
    }
  }
}

.jump-to-page {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'PingFang SC', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
  background: #e3e3e3;
  padding: 2px 16px;
  border-radius: 3px;
  line-height: 22px;
  height: 34px;
  width: 160px;
  flex-shrink: 0;

  span {
    white-space: nowrap;
    line-height: 22px;
    font-weight: 400;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.6);
  }

  .jump-input {
    flex: 1 0 0;
    min-width: 0;

    :deep(.v-field) {
      background: #fff !important;
      border: 1px solid #dcdcdc !important;
      border-radius: 3px !important;
      min-height: auto !important;
      height: auto !important;
      margin: 0 !important;
      box-shadow: none !important;
      display: flex !important;
      align-items: center !important;
    }

    :deep(.v-field__input) {
      padding: 0 !important;
      text-align: center !important;
      font-family: 'PingFang SC', sans-serif !important;
      font-size: 14px !important;
      font-weight: 400 !important;
      color: rgba(0, 0, 0, 0.9) !important;
      min-height: auto !important;
      height: 22px !important;
      line-height: 22px !important;
    }

    :deep(.v-field__outline) {
      display: none !important;
    }

    :deep(input) {
      text-align: center !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    /* 隐藏数字输入框的上下箭头 */
    :deep(input[type="number"]) {

      &::-webkit-inner-spin-button,
      &::-webkit-outer-spin-button {
        -webkit-appearance: none !important;
        margin: 0 !important;
        display: none !important;
      }
    }
  }
}

:deep(.v-input__details) {
  display: none;
}

:deep(.v-table__wrapper) {
  tr th {
    font-family: "HarmonyOS Sans SC", sans-serif;
    font-size: 13px;
    font-weight: 400;
    font-style: normal;
    line-height: 16px;
    letter-spacing: 0px;
    color: #96A5B8;
  }

  tbody tr td {
    font-family: "HarmonyOS Sans SC", sans-serif;
    font-size: 14px;
    font-weight: 400;
    font-style: normal;
    line-height: 100%;
    letter-spacing: 0px;
    color: #444A6D;
  }
}
</style>

<style lang="scss">
/* 电表类型标签：下拉选项与选中态共用 */
.meter-tag {
  flex-shrink: 0;
  padding: 0 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  white-space: nowrap;
  background: rgba(46, 46, 46, 0.06);
  color: rgba(46, 46, 46, 0.65);

  &.is-main {
    background: rgba(250, 140, 22, 0.12);
    color: #d46b08;
  }

  &.is-gateway {
    background: rgba(58, 104, 255, 0.1);
    color: #3a68ff;
  }

  &.is-grid {
    background: rgba(0, 180, 120, 0.12);
    color: #009467;
  }

  &.is-measure {
    background: rgba(114, 46, 209, 0.1);
    color: #722ed1;
  }

  &.is-load {
    background: rgba(19, 158, 190, 0.12);
    color: #0e7c96;
  }
}

.meter-data-filter-select-menu {
  border-radius: 10px !important;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1) !important;
  margin-top: 4px !important;
  overflow: hidden !important;
  border: 1px solid rgba(224, 228, 231, 1) !important;

  .v-list {
    background: #fff !important;
    border-radius: 10px !important;
    padding: 4px 0 !important;
  }

  /* 分组标题：类型名 + 该组数量，与选项在视觉层级上拉开距离 */
  .meter-group-header {
    display: flex !important;
    align-items: center;
    gap: 6px;
    min-height: 28px !important;
    padding: 6px 12px 4px !important;
    background: #fafbfc;
    color: rgba(46, 46, 46, 0.45) !important;
    font-family: 'PingFang SC', sans-serif;
    font-size: 13px;
    font-weight: 500;
    opacity: 1 !important;
  }

  .meter-group-count {
    padding: 0 5px;
    border-radius: 8px;
    background: rgba(46, 46, 46, 0.06);
    font-size: 11px;
    line-height: 16px;
  }

  .meter-option {
    min-height: auto !important;
    padding: 9px 14px !important;
    color: rgba(46, 46, 46, 1) !important;
    font-family: 'PingFang SC', sans-serif !important;
    cursor: pointer !important;
    transition: background-color 0.2s ease !important;

    &:hover {
      background-color: #f5f7fa !important;
    }

    &.v-list-item--active {
      background-color: rgba(58, 104, 255, 0.06) !important;

      .meter-option-name {
        color: #3a68ff !important;
        font-weight: 500;
      }
    }

    /* 「全部电表」是聚合项，与具体电表之间加一条分隔 */
    &.meter-option--all {
      border-bottom: 1px solid #f0f2f5;
    }
  }

  .meter-option-main {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .meter-option-name {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 15px;
    line-height: 22px;
  }
}

/* 时间间隔 / 数据模式下拉：字号与电表下拉保持一致 */
.meter-data-interval-dropdown {
  .el-select-dropdown__item {
    font-family: "HarmonyOS Sans SC", sans-serif;
    font-size: 15px;
    line-height: 34px;
    height: 34px;
  }
}
</style>
